/**
 * Name einer Zahlungsvorlage = "[Kommentar] - [Titel]" (Nutzerentscheidung 01.10.2026).
 *
 * Zwillingsdatei von backend/src/domaene/zahlungsvorlagenTitel.ts (dort die ausfuehrliche Regel).
 * Der Titel wird NIE gespeichert oder eingegeben, sondern aus den Raten in ihrer Reihenfolge erzeugt:
 * "80 % 20 Tage nach ETD", Versatz 0 -> "80 % bei ETD", negativ -> "80 % 10 Tage vor ETD", Raten
 * mit " | " verbunden; ohne Kommentar steht nur der Titel. Beide Dateien werden von derselben
 * Faelletabelle geprueft (shared/zahlungsvorlagen-titel-faelle.json) - laufen sie auseinander, wird
 * eine der beiden Testdateien rot.
 *
 * Das Raten-Label (`rows[].label`) geht in den Titel NICHT ein: es wird nicht mehr eingegeben, alte
 * Zeilen tragen es weiter.
 */
import { ANKER_ANZEIGENAME, HANDDATUM_ANZEIGENAME } from "./anker.ts";

export interface TitelRate {
  pct?: unknown;
  anchor?: unknown;
  offsetDays?: unknown;
}

export function ankerAnzeigename(anchor: unknown): string {
  if (anchor === null || anchor === undefined || anchor === "") return HANDDATUM_ANZEIGENAME;
  const schluessel = String(anchor);
  return Object.hasOwn(ANKER_ANZEIGENAME, schluessel)
    ? ANKER_ANZEIGENAME[schluessel as keyof typeof ANKER_ANZEIGENAME]
    : schluessel;
}

function tage(anzahl: number): string {
  return `${anzahl} ${anzahl === 1 ? "Tag" : "Tage"}`;
}

/** Der Zeitteil einer Rate ohne Prozent: "20 Tage nach ETD", "bei ETD", "10 Tage vor ETA". */
export function ratenBeschreibung(rate: TitelRate): string {
  const anker = ankerAnzeigename(rate.anchor);
  const roh = typeof rate.offsetDays === "string" ? rate.offsetDays.trim() : rate.offsetDays;
  const versatz = typeof roh === "number" || typeof roh === "string" ? Number(roh) : 0;
  const tageZahl = Number.isInteger(versatz) ? versatz : 0;
  if (tageZahl === 0) return `bei ${anker}`;
  if (tageZahl > 0) return `${tage(tageZahl)} nach ${anker}`;
  return `${tage(-tageZahl)} vor ${anker}`;
}

/** Eine Rate, z. B. "80 % 20 Tage nach ETD"; Dezimalzahlen mit deutschem Komma ("50,5 %"). Ein leerer Prozentwert (Alt-Vorlagen wie "x deposit by TT") ergibt "offen bei ETD" statt " % bei ETD". */
export function rateTitel(rate: TitelRate): string {
  // Gespeichert wird mit Punkt ("50.5"), angezeigt mit deutschem Komma.
  const pct = String(rate.pct ?? "").trim().replace(".", ",");
  return pct === "" ? `offen ${ratenBeschreibung(rate)}` : `${pct} % ${ratenBeschreibung(rate)}`;
}

/** Alle Raten in ihrer Reihenfolge, mit " | " verbunden. */
export function ratenTitel(rows: readonly TitelRate[]): string {
  return rows.map(rateTitel).join(" | ");
}

/** "[Kommentar] - [Titel]"; ohne (oder mit nur aus Leerraum bestehendem) Kommentar nur der Titel. */
export function vorlagenName(comment: string | null | undefined, rows: readonly TitelRate[]): string {
  const titel = ratenTitel(rows);
  const kommentar = (comment ?? "").trim();
  return kommentar === "" ? titel : `${kommentar} - ${titel}`;
}
